import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api, formatApiErrorDetail, formatMoney, PAYMENT_METHODS } from "@/lib/api";
import { FileText, Wallet, X, Activity, Plus, Trash2, AlertTriangle, CheckCircle2, Heart, FileWarning } from "lucide-react";
import { toast } from "sonner";
import EvolutionTemplatePicker from "@/components/EvolutionTemplatePicker";
import { useBranding } from "@/context/BrandingContext";
import { generateReceiptPDF } from "@/lib/receiptPdf";

const COURTESY_REASONS = [
  { key: "cortesia_familiar", label: "Cortesía familiar" },
  { key: "garantia",          label: "Garantía" },
  { key: "reposicion",        label: "Reposición" },
  { key: "otro",              label: "Otro" },
];

const PRICE_CHANGE_REASONS = [
  "Promoción",
  "Paciente recurrente",
  "Cortesía parcial",
  "Reajuste",
  "Otro",
];

export default function FinalizeAppointmentDialog({ open, onClose, appointment, doctor, doctorName, onFinalized }) {
  const { branding } = useBranding();
  const [planItems, setPlanItems] = useState([]);
  const [completed, setCompleted] = useState({});
  const [evol, setEvol] = useState({ fecha: new Date().toISOString().slice(0, 10), diente: "", diagnostico: "", procedimiento: "", observaciones: "" });
  const [items, setItems] = useState([]);          // tabla editable de cobro
  const [settlement, setSettlement] = useState(null);  // cobrado | cxc | cortesia
  const [payments, setPayments] = useState([{ method: "efectivo", amount: "" }]);
  const [applyAnticipo, setApplyAnticipo] = useState(false);
  const [applyCredit, setApplyCredit] = useState(false);
  const [balance, setBalance] = useState({ anticipo_balance: 0, ar_open_total: 0 });
  const [excedenteToAnticipo, setExcedenteToAnticipo] = useState(true);
  const [saldoToCxc, setSaldoToCxc] = useState(true);
  const [courtesyReason, setCourtesyReason] = useState("");
  const [courtesyNotes, setCourtesyNotes] = useState("");
  const [genReceipt, setGenReceipt] = useState(true);
  const [genDian, setGenDian] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  const [cashOpen, setCashOpen] = useState(false);

  useEffect(() => {
    if (!open || !appointment?.patient_id) return;
    api.get(`/patients/${appointment.patient_id}/plan`).then((r) => {
      const pending = r.data.filter((i) => i.status !== "completado");
      setPlanItems(pending);
      // pre-llenar items de cobro: si la cita tiene plan_item_id usarlo, sino crear uno desde el tratamiento de la cita
      const seedItems = [{
        plan_item_id: null,
        treatment_id: appointment.treatment_id || null,
        treatment_name: appointment.treatment_name || "Tratamiento",
        tooth: "",
        price_plan: Number(appointment.price) || 0,
        price_charged: Number(appointment.price) || 0,
        change_reason: "",
      }];
      setItems(seedItems);
    }).catch(() => {});
    api.get(`/patients/${appointment.patient_id}`).then((r) => setPatient(r.data.patient)).catch(() => {});
    api.get(`/patients/${appointment.patient_id}/balance`).then((r) => setBalance(r.data)).catch(() => {});
    api.get("/cashbox/today").then((r) => setCashOpen(!!(r.data.session && r.data.session.status === "abierta"))).catch(() => {});
    setCompleted({});
    setEvol({
      fecha: new Date().toISOString().slice(0, 10),
      diente: "", diagnostico: "",
      procedimiento: appointment.treatment_name || "",
      observaciones: "",
    });
    setSettlement(null);
    setPayments([{ method: "efectivo", amount: "" }]);
    setApplyAnticipo(false);
    setApplyCredit(false);
    setCourtesyReason("");
    setCourtesyNotes("");
    setGenReceipt(true);
    setGenDian(false);
    setSendEmail(false);
    setSendWhatsapp(false);
  }, [open, appointment]);

  // Re-pre-llenar items cuando se marcan items del plan
  useEffect(() => {
    const completedFromPlan = planItems.filter((i) => completed[i.id]).map((i) => ({
      plan_item_id: i.id,
      treatment_id: i.treatment_id || null,
      treatment_name: i.treatment_name || i.name,
      tooth: i.tooth || "",
      price_plan: Number(i.price) || 0,
      price_charged: Number(i.price) || 0,
      change_reason: "",
    }));
    if (completedFromPlan.length > 0) {
      setItems(completedFromPlan);
    } else if (appointment) {
      setItems([{
        plan_item_id: null,
        treatment_id: appointment.treatment_id || null,
        treatment_name: appointment.treatment_name || "Tratamiento",
        tooth: "",
        price_plan: Number(appointment.price) || 0,
        price_charged: Number(appointment.price) || 0,
        change_reason: "",
      }]);
    }
  }, [completed, planItems]);

  if (!open || !appointment?.id) return null;

  const toggleItem = (id) => setCompleted((c) => ({ ...c, [id]: !c[id] }));
  const completedItemsList = planItems.filter((i) => completed[i.id]);

  const totalCharged = items.reduce((acc, it) => acc + (Number(it.price_charged) || 0), 0);

  const appliedAnticipoAmount = applyAnticipo ? Math.min(balance.anticipo_balance || 0, totalCharged) : 0;
  const appliedCreditAmount = applyCredit ? Math.min(balance.ar_open_total || 0, Math.max(0, totalCharged - appliedAnticipoAmount)) : 0;
  const netToCollect = Math.max(0, totalCharged - appliedAnticipoAmount - appliedCreditAmount);
  const sumPayments = payments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const diff = sumPayments - netToCollect;  // + excedente, - saldo restante

  const itemHasPriceChangeWithoutReason = items.some(
    (it) => Math.abs((Number(it.price_charged) || 0) - (Number(it.price_plan) || 0)) > 0.01 && !(it.change_reason || "").trim()
  );

  const canFinalize = (() => {
    if (!evol.procedimiento.trim()) return false;
    if (!settlement) return false;
    if (settlement === "cobrado") {
      if (!cashOpen) return false;
      if (totalCharged <= 0) return false;
      if (itemHasPriceChangeWithoutReason) return false;
      if (Math.abs(diff) > 0.01) {
        if (diff > 0 && !excedenteToAnticipo) return false;
        if (diff < 0 && !saldoToCxc) return false;
      }
    }
    if (settlement === "cortesia") {
      if (!courtesyReason) return false;
    }
    return true;
  })();

  const setItemField = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };

  const addPaymentLine = () => setPayments((p) => [...p, { method: "efectivo", amount: "" }]);
  const removePaymentLine = (idx) => setPayments((p) => p.filter((_, i) => i !== idx));
  const setPaymentField = (idx, key, val) => setPayments((p) => p.map((line, i) => i === idx ? { ...line, [key]: val } : line));

  const submit = async () => {
    if (!canFinalize) return;
    setSaving(true);
    try {
      const body = {
        evolution: {
          fecha: evol.fecha,
          diente: evol.diente || "",
          diagnostico: evol.diagnostico || "",
          procedimiento: evol.procedimiento.trim(),
          observaciones: evol.observaciones || "",
        },
        completed_plan_items: completedItemsList.map((i) => i.id),
        items: items.map((it) => ({
          plan_item_id: it.plan_item_id || null,
          treatment_id: it.treatment_id || null,
          treatment_name: it.treatment_name || "",
          tooth: it.tooth || "",
          price_plan: Number(it.price_plan) || 0,
          price_charged: Number(it.price_charged) || 0,
          change_reason: it.change_reason || "",
        })),
        settlement_type: settlement,
        payments: settlement === "cobrado"
          ? payments.filter((p) => Number(p.amount) > 0).map((p) => ({ method: p.method, amount: Number(p.amount) }))
          : [],
        applied_anticipo: appliedAnticipoAmount,
        applied_credit: appliedCreditAmount,
        excedente_to_anticipo: excedenteToAnticipo,
        saldo_to_cxc: saldoToCxc,
        courtesy_reason: settlement === "cortesia" ? courtesyReason : null,
        courtesy_notes: settlement === "cortesia" ? courtesyNotes : "",
        generate_receipt: genReceipt,
        generate_dian: genDian,
        send_email: sendEmail,
        send_whatsapp: sendWhatsapp,
      };
      const { data } = await api.post(`/appointments/${appointment.id}/finalize`, body);

      // Generar PDF en cliente
      if (genReceipt) {
        generateReceiptPDF({
          patient: patient || { name: appointment.patient?.name },
          appointment,
          evolucion: evol,
          items,
          settlement,
          payments: payments.filter((p) => Number(p.amount) > 0),
          applied: { anticipo: appliedAnticipoAmount, credit: appliedCreditAmount },
          total: totalCharged,
          ar: data.ar_id ? { id: data.ar_id } : null,
          courtesy: settlement === "cortesia" ? { reason: courtesyReason, notes: courtesyNotes } : null,
          doctor,
          doctorName,
          clinicName: localStorage.getItem("df_clinic_name") || "DentCloud",
          branding,
          receiptNumber: data?.receipt?.number,
        });
      }

      const msg = settlement === "cobrado" ? "Cita finalizada y cobrada"
        : settlement === "cxc" ? "Cita finalizada · queda como CxC"
        : "Cita finalizada · cortesía registrada";
      toast.success(msg);
      onFinalized && onFinalized();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error finalizando");
    } finally {
      setSaving(false);
    }
  };

  // Usamos Radix Dialog primitives. Esto integra el modal con la pila de
  // focus management de Radix: cuando este Dialog abre encima del Sheet,
  // Radix suspende el FocusTrap del Sheet y permite que los inputs y
  // dropdowns dentro de este modal reciban focus correctamente (incluido el
  // input "Buscar plantilla" del EvolutionTemplatePicker).
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!v && onClose) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
          style={{ zIndex: 999999 }}
        />
        <DialogPrimitive.Content
          className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 outline-none"
          style={{ zIndex: 999999 }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            // Cerrar al hacer click en el backdrop (fuera de la caja del modal)
            // pero NO si el click viene del Sheet padre (que también está
            // técnicamente "fuera"). Solo cerramos si el click es en la
            // overlay propia.
            const target = e.target;
            if (target && target.getAttribute && target.getAttribute("data-radix-dialog-overlay") !== null) {
              // Permitir cierre normal
            }
          }}
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Registrar trabajo realizado</DialogPrimitive.Title>
          <div
            className="bg-card rounded-sm border border-border w-full max-w-3xl flex flex-col overflow-hidden shadow-2xl"
            style={{ maxWidth: "min(90vw, 768px)", maxHeight: "90vh", WebkitOverflowScrolling: "touch" }}
            data-testid="finalize-dialog"
          >
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-border flex items-center justify-between bg-card shrink-0">
          <div className="min-w-0 pr-2">
            <div className="label-eyebrow text-primary">Finalizar atención</div>
            <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight leading-tight truncate">Registrar trabajo realizado</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted shrink-0 min-h-[40px] min-w-[40px] flex items-center justify-center" aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 space-y-5"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          {/* Evolución */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                <h3 className="font-display font-bold text-base">Evolución clínica</h3>
              </div>
              <EvolutionTemplatePicker
                onPick={(t) => setEvol((prev) => ({
                  ...prev,
                  diagnostico: t.diagnostico || prev.diagnostico,
                  procedimiento: t.procedimiento,
                  observaciones: t.observaciones || prev.observaciones,
                }))}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label-eyebrow block mb-1">Fecha</label>
                <input type="date" className="input-flat font-mono" value={evol.fecha} onChange={(e) => setEvol({ ...evol, fecha: e.target.value })} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Diente (FDI)</label>
                <input className="input-flat font-mono" placeholder="16" value={evol.diente} onChange={(e) => setEvol({ ...evol, diente: e.target.value })} />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">Diagnóstico</label>
                <input className="input-flat" value={evol.diagnostico} onChange={(e) => setEvol({ ...evol, diagnostico: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Procedimiento realizado *</label>
              <textarea required rows={3} className="input-flat h-auto py-2" placeholder="Describe el trabajo realizado..."
                value={evol.procedimiento} onChange={(e) => setEvol({ ...evol, procedimiento: e.target.value })} data-testid="finalize-procedimiento" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Observaciones</label>
              <textarea rows={2} className="input-flat h-auto py-2" value={evol.observaciones} onChange={(e) => setEvol({ ...evol, observaciones: e.target.value })} />
            </div>
          </section>

          {/* Plan items */}
          {planItems.length > 0 && (
            <section className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <h3 className="font-display font-bold text-base">Marcar tratamientos del plan completados</h3>
                <span className="text-xs text-muted-foreground">(opcional)</span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-border rounded-sm">
                {planItems.map((it) => {
                  const isOn = !!completed[it.id];
                  return (
                    <label key={it.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted ${isOn ? "bg-primary/5" : ""}`}>
                      <input type="checkbox" checked={isOn} onChange={() => toggleItem(it.id)} className="w-4 h-4 accent-primary" data-testid={`plan-item-${it.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{it.treatment_name || it.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{it.tooth ? `Diente ${it.tooth} · ` : ""}{formatMoney(it.price || 0)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>
          )}

          {/* Liquidación: 3 opciones obligatorias */}
          <section className="space-y-3 pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              <h3 className="font-display font-bold text-base">¿Qué pasa con el cobro?</h3>
              <span className="text-[10px] uppercase font-bold tracking-widest bg-destructive/10 text-destructive px-2 py-0.5 rounded-sm">Obligatorio</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="settlement-options">
              {[
                { key: "cobrado", label: "Cobrado ahora", icon: Wallet, desc: "Registrar el pago en caja" },
                { key: "cxc", label: "Cuenta por cobrar", icon: FileWarning, desc: "El paciente debe el saldo" },
                { key: "cortesia", label: "Cortesía / no se cobra", icon: Heart, desc: "Sin pago, requiere motivo" },
              ].map((opt) => {
                const Active = settlement === opt.key;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`settlement-${opt.key}`}
                    onClick={() => setSettlement(opt.key)}
                    className={`p-3 rounded-sm border text-left transition ${Active ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:bg-muted"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${Active ? "text-primary" : "text-muted-foreground"}`} />
                      <div className={`text-sm font-bold ${Active ? "text-primary" : ""}`}>{opt.label}</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Tabla editable de procedimientos cobrados (visible si hay decisión tomada) */}
          {settlement && (
            <section className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-base">Procedimientos {settlement === "cortesia" ? "no cobrados" : "a cobrar"}</h3>
                <span className="text-xs text-muted-foreground font-mono">Total: <span className="font-bold text-foreground">{formatMoney(totalCharged)}</span></span>
              </div>
              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-xs" data-testid="items-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-widest text-[10px]">Procedimiento</th>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-widest text-[10px] w-16">Diente</th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-widest text-[10px] w-24">Plan</th>
                      <th className="px-2 py-2 text-right font-bold uppercase tracking-widest text-[10px] w-28">Cobrado</th>
                      <th className="px-2 py-2 text-left font-bold uppercase tracking-widest text-[10px] w-40">Motivo si difiere</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const differ = Math.abs((Number(it.price_charged) || 0) - (Number(it.price_plan) || 0)) > 0.01;
                      return (
                        <tr key={idx} className="border-t border-border">
                          <td className="px-2 py-1.5">
                            <input className="input-flat !h-8 text-xs" value={it.treatment_name} onChange={(e) => setItemField(idx, "treatment_name", e.target.value)} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input className="input-flat !h-8 text-xs font-mono" value={it.tooth} onChange={(e) => setItemField(idx, "tooth", e.target.value)} />
                          </td>
                          <td className="px-2 py-1.5 font-mono text-right text-muted-foreground">{formatMoney(it.price_plan || 0)}</td>
                          <td className="px-2 py-1.5">
                            <input type="number" className={`input-flat !h-8 text-xs font-mono text-right ${differ ? "ring-1 ring-amber-500" : ""}`}
                              value={it.price_charged}
                              onChange={(e) => setItemField(idx, "price_charged", e.target.value)}
                              data-testid={`item-price-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            {differ && (
                              <select
                                className="input-flat !h-8 text-xs"
                                value={it.change_reason}
                                onChange={(e) => setItemField(idx, "change_reason", e.target.value)}
                                data-testid={`item-reason-${idx}`}
                              >
                                <option value="">— Selecciona —</option>
                                {PRICE_CHANGE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                              </select>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {itemHasPriceChangeWithoutReason && (
                <div className="text-[11px] text-destructive flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Hay precios modificados sin motivo. Indícalo para continuar.</div>
              )}
            </section>
          )}

          {/* Saldos previos */}
          {settlement === "cobrado" && (balance.anticipo_balance > 0 || balance.ar_open_total > 0) && (
            <section className="space-y-2 pt-2 border-t border-border">
              <h3 className="font-display font-bold text-base">Saldos previos del paciente</h3>
              {balance.anticipo_balance > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={applyAnticipo} onChange={(e) => setApplyAnticipo(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="apply-anticipo" />
                  Aplicar anticipo disponible: <span className="font-mono font-bold">{formatMoney(balance.anticipo_balance)}</span>
                </label>
              )}
              {balance.ar_open_total > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={applyCredit} onChange={(e) => setApplyCredit(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="apply-credit" />
                  Aplicar saldo a favor: <span className="font-mono font-bold">{formatMoney(balance.ar_open_total)}</span>
                </label>
              )}
              {(applyAnticipo || applyCredit) && (
                <div className="text-xs text-muted-foreground font-mono">Total ajustado a cobrar: <span className="font-bold text-foreground">{formatMoney(netToCollect)}</span></div>
              )}
            </section>
          )}

          {/* Pagos mixtos */}
          {settlement === "cobrado" && (
            <section className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-base">Métodos de pago</h3>
                <button type="button" onClick={addPaymentLine} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline flex items-center gap-1" data-testid="add-payment-line">
                  <Plus className="w-3 h-3" /> Agregar método
                </button>
              </div>
              {!cashOpen && (
                <div className="flex items-center gap-2 p-2 rounded-sm bg-destructive/10 border border-destructive/30 text-xs text-destructive">
                  <AlertTriangle className="w-4 h-4" /> Caja del día cerrada. Abre la caja para registrar cobros, o usa CxC/Cortesía.
                </div>
              )}
              <div className="space-y-2">
                {payments.map((line, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select className="input-flat !h-9 text-xs flex-1" value={line.method} onChange={(e) => setPaymentField(idx, "method", e.target.value)} data-testid={`payment-method-${idx}`}>
                      {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                    <div className="flex items-center gap-1 flex-1">
                      <span className="font-mono text-sm text-muted-foreground">$</span>
                      <input type="number" step="1000" className="input-flat !h-9 font-mono"
                        value={line.amount}
                        onChange={(e) => setPaymentField(idx, "amount", e.target.value)}
                        data-testid={`payment-amount-${idx}`} />
                    </div>
                    {payments.length > 1 && (
                      <button type="button" onClick={() => removePaymentLine(idx)} className="h-9 w-9 rounded-sm border border-border text-destructive hover:bg-destructive/10 flex items-center justify-center" aria-label="Eliminar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-1">
                <div className="text-muted-foreground">Total a cobrar:</div>
                <div className="text-right font-bold">{formatMoney(netToCollect)}</div>
                <div className="text-muted-foreground">Suma métodos:</div>
                <div className="text-right font-bold">{formatMoney(sumPayments)}</div>
                <div className={`font-bold ${Math.abs(diff) <= 0.01 ? "text-green-700" : diff > 0 ? "text-amber-600" : "text-destructive"}`}>
                  {Math.abs(diff) <= 0.01 ? "✓ Cuadrado" : diff > 0 ? `Excedente: ${formatMoney(diff)}` : `Faltante: ${formatMoney(-diff)}`}
                </div>
                <div></div>
              </div>
              {diff < -0.01 && (
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={saldoToCxc} onChange={(e) => setSaldoToCxc(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="saldo-to-cxc" />
                  Registrar saldo restante <span className="font-mono font-bold">{formatMoney(-diff)}</span> como CxC
                </label>
              )}
              {diff > 0.01 && (
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={excedenteToAnticipo} onChange={(e) => setExcedenteToAnticipo(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="excedente-to-anticipo" />
                  Registrar excedente <span className="font-mono font-bold">{formatMoney(diff)}</span> como anticipo
                </label>
              )}
            </section>
          )}

          {/* Cortesía */}
          {settlement === "cortesia" && (
            <section className="space-y-2 pt-2 border-t border-border">
              <h3 className="font-display font-bold text-base">Motivo de cortesía *</h3>
              <select className="input-flat" value={courtesyReason} onChange={(e) => setCourtesyReason(e.target.value)} data-testid="courtesy-reason">
                <option value="">— Selecciona —</option>
                {COURTESY_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <textarea rows={2} className="input-flat h-auto py-2" placeholder="Notas adicionales (opcional)" value={courtesyNotes} onChange={(e) => setCourtesyNotes(e.target.value)} data-testid="courtesy-notes" />
              <div className="text-[11px] text-muted-foreground">Esta acción quedará registrada en log auditable con tu usuario.</div>
            </section>
          )}

          {/* Documento a generar */}
          <section className="space-y-2 pt-2 border-t border-border">
            <h3 className="font-display font-bold text-base">Documento a generar</h3>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={genReceipt} onChange={(e) => setGenReceipt(e.target.checked)} className="w-4 h-4 accent-primary" />
              Recibo de caja (PDF)
            </label>
            <label className="flex items-center gap-2 text-sm opacity-50 cursor-not-allowed" title="Requiere proveedor DIAN configurado">
              <input type="checkbox" checked={genDian} disabled className="w-4 h-4 accent-primary" />
              Factura electrónica DIAN <span className="text-[10px] text-muted-foreground">(próximamente)</span>
            </label>
            <label className="flex items-center gap-2 text-sm opacity-50 cursor-not-allowed" title="Próximamente">
              <input type="checkbox" checked={sendEmail} disabled onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 accent-primary" />
              Enviar por correo <span className="text-[10px] text-muted-foreground">(próximamente)</span>
            </label>
            <label className="flex items-center gap-2 text-sm opacity-50 cursor-not-allowed" title="Próximamente">
              <input type="checkbox" checked={sendWhatsapp} disabled onChange={(e) => setSendWhatsapp(e.target.checked)} className="w-4 h-4 accent-primary" />
              Enviar por WhatsApp <span className="text-[10px] text-muted-foreground">(próximamente)</span>
            </label>
          </section>
        </div>

        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-border flex flex-col-reverse sm:flex-row gap-2 bg-card shrink-0">
          <button onClick={onClose} className="px-4 h-11 min-h-[44px] rounded-sm border border-border hover:bg-muted text-sm font-bold uppercase tracking-widest sm:flex-none">Cancelar</button>
          <button
            onClick={submit}
            disabled={saving || !canFinalize}
            className="btn-primary !h-11 min-h-[44px] flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="finalize-confirm"
          >
            <FileText className="w-4 h-4" /> {saving ? "Guardando…" : "Finalizar y generar recibo"}
          </button>
        </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
